/**
 * Everything a proposed device command would write, as the server works it out
 * from the devices at the moment the card is opened.
 *
 * None of this comes from the chat message. A device command writes settings
 * nobody asked about, and those settings move; a card rendered from what the AI
 * saw would keep asserting a device is as it was when the answer was written.
 * The fingerprint is what ties this reading to the confirmation that follows it.
 */
export interface ProposedActionChoice {
  value: string;
  label: string;
}

export interface ProposedActionField {
  fieldKey: string;
  label: string;
  editor: string;
  currentDisplay: string;
  newDisplay: string;
  currentRaw: string;
  newRaw: string;
  currentVaries: boolean;
  isChanged: boolean;
  /** The command writes this, but nobody asked to change it. */
  isCarriedForward: boolean;
  /** Supplied by the server so the card cannot offer a value the server refuses. */
  choices: ProposedActionChoice[] | null;
}

export interface ProposedActionTarget {
  uid: number;
  transport: string;
  isConnected: boolean;
  pendingCommandCount: number;
}

export interface ProposedActionDetail {
  actionId: string;
  commandType: string;
  summary: string;
  expiresAtUtc: string;
  requiresTypedConfirmation: boolean;
  fingerprint: string;
  baselineReportedAtUtc: string | null;
  fields: ProposedActionField[];
  targets: ProposedActionTarget[];
  warnings: string[];
}

export interface ProposedActionAmendment {
  fieldKey: string;
  newRaw: string;
}

export interface ProposedActionConfirmation {
  fingerprint: string;
  amendments: ProposedActionAmendment[];
  uids: number[] | null;
}

export const ProposedActionFieldEditor = {
  PowerMode: 'powerMode',
  ConnectSchedule: 'connectSchedule',
  Sensitivity: 'sensitivity',
  ElfFrequency: 'elfFrequency',
  Toggle: 'toggle',
  DateTimeUtc: 'dateTimeUtc',
  ReadOnly: 'readOnly',
} as const;

/**
 * Returns whether the operator can change this setting. A read-only field is
 * still listed, because the command still writes it.
 */
export function isEditableField(field: ProposedActionField): boolean {
  return field.editor !== ProposedActionFieldEditor.ReadOnly;
}

/**
 * Reduces the card back to the edits the operator actually made, so a
 * confirmation says what was changed rather than restating every setting.
 */
export function collectAmendments(
  original: ProposedActionField[],
  edited: Record<string, string>
): ProposedActionAmendment[] {
  return original
    .filter(
      (field) =>
        isEditableField(field) &&
        edited[field.fieldKey] !== undefined &&
        edited[field.fieldKey] !== field.newRaw
    )
    .map((field) => ({
      fieldKey: field.fieldKey,
      newRaw: edited[field.fieldKey],
    }));
}
